// Shared Canvas 2D helpers for the backtest charts (U6/KTD2). Hand-rolled — the repo deliberately has
// zero chart dependencies. Colors reuse the candlestick chart's literals so the visual language matches
// (canvas can't read CSS vars).

export const COLORS = {
  line: '#00d4ff',
  gain: '#00ff88',
  loss: '#ff3b3b',
  grid: '#1e1f22',
  label: '#8a8b8d',
  bg: '#0a0b0d',
  crosshair: '#8a8b8d',
};

export const PADDING = { top: 16, right: 56, bottom: 24, left: 8 };

// Build linear scales for a [time, value] series within the canvas, given padded plot bounds.
export function buildScales(points, width, height, { valueMin, valueMax } = {}) {
  const plotW = width - PADDING.left - PADDING.right;
  const plotH = height - PADDING.top - PADDING.bottom;
  const n = points.length;

  let vMin = valueMin ?? Infinity;
  let vMax = valueMax ?? -Infinity;
  if (valueMin === undefined || valueMax === undefined) {
    for (const p of points) {
      if (p.value < vMin) vMin = p.value;
      if (p.value > vMax) vMax = p.value;
    }
  }
  if (!Number.isFinite(vMin) || !Number.isFinite(vMax)) {
    vMin = 0;
    vMax = 1;
  }
  if (vMin === vMax) {
    vMin -= 1;
    vMax += 1;
  }

  const xOf = (i) => PADDING.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yOf = (v) => PADDING.top + (1 - (v - vMin) / (vMax - vMin)) * plotH;
  return { plotW, plotH, vMin, vMax, xOf, yOf };
}

// Clear + paint the background.
export function clearCanvas(ctx, width, height) {
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);
}

// Horizontal gridlines + right-edge value labels (formatted by `fmt`).
export function drawGrid(ctx, width, height, scales, fmt, rows = 4) {
  const { vMin, vMax } = scales;
  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  ctx.font = '11px sans-serif';
  ctx.textAlign = 'right';
  ctx.fillStyle = COLORS.label;
  for (let i = 0; i <= rows; i++) {
    const v = vMax - ((vMax - vMin) * i) / rows;
    const y = scales.yOf(v);
    ctx.beginPath();
    ctx.moveTo(PADDING.left, y);
    ctx.lineTo(width - PADDING.right, y);
    ctx.stroke();
    ctx.fillText(fmt(v), width - 5, y + 3);
  }
}

// X-axis time labels at ~6 evenly-spaced points.
export function drawTimeLabels(ctx, points, height, scales) {
  if (points.length === 0) return;
  ctx.fillStyle = COLORS.label;
  ctx.font = '10px sans-serif';
  ctx.textAlign = 'center';
  const interval = Math.max(1, Math.ceil(points.length / 6));
  points.forEach((p, i) => {
    if (i % interval === 0) {
      const x = scales.xOf(i);
      const d = new Date(p.time);
      const label = Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      ctx.fillText(label, x, height - 6);
    }
  });
}

// Crosshair vertical line + a value/time tooltip box at the hovered index.
export function drawCrosshair(ctx, points, index, width, height, scales, fmt) {
  if (index < 0 || index >= points.length) return;
  const x = scales.xOf(index);
  const p = points[index];
  const y = scales.yOf(p.value);

  ctx.strokeStyle = COLORS.crosshair;
  ctx.globalAlpha = 0.4;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(x, PADDING.top);
  ctx.lineTo(x, height - PADDING.bottom);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;

  // Point marker.
  ctx.fillStyle = COLORS.line;
  ctx.beginPath();
  ctx.arc(x, y, 3, 0, Math.PI * 2);
  ctx.fill();

  // Tooltip box.
  const d = new Date(p.time);
  const timeStr = Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const text = `${timeStr}  ${fmt(p.value)}`;
  ctx.font = '11px sans-serif';
  const tw = ctx.measureText(text).width + 12;
  let bx = x + 8;
  if (bx + tw > width - PADDING.right) bx = x - tw - 8;
  const by = PADDING.top + 4;
  ctx.fillStyle = 'rgba(20,21,24,0.92)';
  ctx.fillRect(bx, by, tw, 20);
  ctx.strokeStyle = COLORS.grid;
  ctx.strokeRect(bx, by, tw, 20);
  ctx.fillStyle = '#e8e9ea';
  ctx.textAlign = 'left';
  ctx.fillText(text, bx + 6, by + 14);
}

// Map a mouse x (canvas px) to the nearest series index.
export function indexFromX(mouseX, n, width) {
  if (n <= 1) return 0;
  const plotW = width - PADDING.left - PADDING.right;
  const ratio = (mouseX - PADDING.left) / plotW;
  return Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1))));
}

// A concise text summary of a series for a canvas aria-label (a11y, KTD/U6).
export function summarize(points, label, fmt) {
  if (!points || points.length < 2) return `${label}: insufficient data.`;
  const first = points[0].value;
  const last = points[points.length - 1].value;
  let peak = -Infinity;
  let trough = Infinity;
  for (const p of points) {
    if (p.value > peak) peak = p.value;
    if (p.value < trough) trough = p.value;
  }
  return `${label}: start ${fmt(first)}, end ${fmt(last)}, peak ${fmt(peak)}, low ${fmt(trough)}, ${points.length} points.`;
}
